import type * as React from "react";
import { Icon, type IconName } from "../icon/Icon";

/**
 * User/company avatar: image, initials, or icon placeholder.
 * Ported from the Wanted Design System bundle (components/data-display/Avatar.jsx).
 * Tellvue addition: `initials` caps how many characters show (Korean names read better with one)
 * and `className` lets the accent helper repaint background and text together.
 */
export interface AvatarProps {
  /** Image URL. */
  src?: string;
  alt?: string;
  /** Name for initials fallback. */
  name?: string;
  /** How many characters of `name` to show. @default 2 */
  initials?: number;
  /** @default "medium" */
  size?: "xsmall" | "small" | "medium" | "large" | "xlarge" | "2xlarge" | number;
  /** @default "circle" */
  shape?: "circle" | "rounded";
  /** Placeholder icon when no src/name. @default "person" */
  icon?: IconName;
  className?: string;
  style?: React.CSSProperties;
}

const SIZES = { xsmall: 24, small: 32, medium: 40, large: 48, xlarge: 64, "2xlarge": 80 } as const;

export function Avatar({
  src,
  alt = "",
  name,
  initials: initialsCount = 2,
  size = "medium",
  shape = "circle",
  icon = "person",
  className,
  style,
}: AvatarProps) {
  const px = typeof size === "number" ? size : (SIZES[size] ?? 40);
  const radius = shape === "circle" ? "var(--radius-full)" : "var(--radius-md)";
  const initials = name ? Array.from(name.trim()).slice(0, initialsCount).join("") : null;
  return (
    <span
      aria-hidden={!src}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: px,
        height: px,
        flex: "none",
        borderRadius: radius,
        overflow: "hidden",
        background: "var(--fill-normal)",
        color: "var(--label-neutral)",
        boxShadow: "inset 0 0 0 1px var(--line-normal-neutral)",
        fontFamily: "var(--font-sans)",
        fontSize: Math.round(px * 0.4),
        fontWeight: 600,
        ...style,
      }}
    >
      {src ? (
        <img src={src} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : initials ? (
        <span>{initials}</span>
      ) : (
        <Icon name={icon} size={Math.round(px * 0.56)} />
      )}
    </span>
  );
}

export default Avatar;
